import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '../hooks/useStudentAuth';
import SEO from '../components/SEO';
import {
  Eye,
  EyeOff,
  ArrowLeft,
  Mail,
  CheckCircle2,
  Circle,
  Loader2,
  Clock,
  BookOpen,
  GraduationCap,
  Users,
} from 'lucide-react';

type Mode = 'login' | 'register' | 'forgot' | 'confirm_pending';

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8L6.2 32.5C9.5 39.1 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

const AppleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const StudentLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    signIn, signUp, signInWithProvider, signOut,
    resetPassword, resendConfirmation, profile, user, loading,
  } = useStudentAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [providerLoading, setProviderLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (user && profile && profile.is_active) {
      navigate('/dashboard', { replace: true });
    } else if (submitting && user && profile && !profile.is_active) {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, profile]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const translateError = (msg: string): string => {
    const lower = msg.toLowerCase();
    if (lower.includes('invalid login credentials')) return 'E-Mail oder Passwort ist falsch.';
    if (lower.includes('email not confirmed') || lower.includes('not confirmed'))
      return 'Bitte bestätige zuerst deine E-Mail-Adresse.';
    if (lower.includes('already registered') || lower.includes('user already'))
      return 'Diese E-Mail-Adresse ist bereits registriert.';
    if (lower.includes('rate limit') || lower.includes('too many'))
      return 'Zu viele Versuche. Bitte warte einen Moment.';
    if (lower.includes('provider is not enabled') || lower.includes('not enabled'))
      return 'Dieser Anmelde-Anbieter ist noch nicht konfiguriert.';
    if (lower.includes('network') || lower.includes('failed to fetch'))
      return 'Netzwerkfehler. Bitte prüfe deine Internetverbindung.';
    return msg;
  };

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };
  const passwordScore = Object.values(passwordChecks).filter(Boolean).length;

  const switchMode = (m: Mode) => { setMode(m); setError(''); setInfo(''); };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setInfo(''); setSubmitting(true);
    try { await signIn(email.trim(), password); }
    catch (err: unknown) {
      setError(translateError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen'));
      setSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setInfo('');
    if (!firstName.trim() || !lastName.trim()) { setError('Bitte Vor- und Nachname eingeben.'); return; }
    if (passwordScore < 3) { setError('Bitte ein stärkeres Passwort wählen (mind. 3 Kriterien).'); return; }
    setSubmitting(true);
    try {
      const { needsEmailConfirmation } = await signUp(email.trim(), password, firstName.trim(), lastName.trim());
      if (needsEmailConfirmation) { setMode('confirm_pending'); setResendCooldown(60); setSubmitting(false); }
    } catch (err: unknown) {
      setError(translateError(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen'));
      setSubmitting(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setInfo(''); setSubmitting(true);
    const trimmedEmail = email.trim();
    try {
      await resetPassword(trimmedEmail);
      setInfo(`Wenn ein Konto für ${trimmedEmail} existiert, haben wir dir einen Reset-Link geschickt.`);
    } catch (err: unknown) {
      setError(translateError(err instanceof Error ? err.message : 'Fehler'));
    } finally { setSubmitting(false); }
  };

  const handleProvider = async (provider: 'google' | 'apple') => {
    setError(''); setProviderLoading(provider);
    // Safety: reset loading state after 12s in case the redirect never fires
    const fallbackTimer = setTimeout(() => setProviderLoading(null), 12000);
    try { await signInWithProvider(provider); }
    catch (err: unknown) {
      clearTimeout(fallbackTimer);
      setError(translateError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen'));
      setProviderLoading(null);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true); setError('');
    try {
      await resendConfirmation(email.trim());
      setInfo('Bestätigungs-E-Mail wurde erneut gesendet.');
      setResendCooldown(60);
    } catch (err: unknown) {
      setError(translateError(err instanceof Error ? err.message : 'Fehler'));
    } finally { setResending(false); }
  };

  const inputClass = 'w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000]/50 outline-none transition-all duration-200 bg-white text-gray-900 placeholder-gray-400 text-sm';

  // ── Pending activation ──
  if (!loading && user && profile && !profile.is_active) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <SEO title="Schüler-Login – Arabicroots" url="/login" noIndex />
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Clock className="h-7 w-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Konto wartet auf Freischaltung</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            Deine Registrierung war erfolgreich. Unser Team aktiviert dein Konto in Kürze —
            du bekommst eine E-Mail sobald dein Zugang freigeschaltet ist.
          </p>
          <button onClick={() => signOut()} className="text-sm text-[#970000] font-semibold hover:underline">
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  // ── Main layout ──
  return (
    <div className="min-h-screen flex">
      <SEO title="Schüler-Login – Arabicroots" description="Melde dich in deinem Arabicroots-Schülerbereich an." url="/login" noIndex />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-[42%] bg-[#970000] flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />

        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-12">
            <ArrowLeft className="h-4 w-4" />
            Zur Website
          </Link>
          <img
            src="/LogoHeader4.png"
            alt="Arabicroots"
            className="h-14 w-auto object-contain brightness-0 invert mb-10"
            onError={e => (e.currentTarget.style.display = 'none')}
          />
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
            Dein Schüler&shy;bereich
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            Greife auf alle deine Kursaufzeichnungen, Materialien und Zertifikate zu.
          </p>
        </div>

        {/* Feature list */}
        <div className="relative space-y-4">
          {[
            { icon: BookOpen, label: 'Alle Aufzeichnungen jederzeit verfügbar' },
            { icon: GraduationCap, label: 'Zertifikate nach Kursabschluss' },
            { icon: Users, label: 'Direkter Kontakt mit deinem Lehrer' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-white" />
              </div>
              <span className="text-white/80 text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 bg-gray-50 flex flex-col">
        {/* Mobile back link */}
        <div className="lg:hidden px-5 pt-5">
          <Link to="/" className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Website
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-sm">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <img src="/LogoHeader4.png" alt="Arabicroots" className="h-16 w-auto object-contain mx-auto" onError={e => (e.currentTarget.style.display = 'none')} />
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">

              {/* ── CONFIRM PENDING ── */}
              {mode === 'confirm_pending' && (
                <div className="text-center py-2">
                  <div className="w-12 h-12 bg-[#970000]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-6 w-6 text-[#970000]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Bestätige deine E-Mail</h3>
                  <p className="text-gray-500 text-sm mb-1">Wir haben dir eine Bestätigungs-E-Mail an</p>
                  <p className="font-semibold text-gray-900 mb-3 break-all text-sm">{email}</p>
                  <p className="text-gray-500 text-sm mb-5">geschickt. Klicke auf den Link darin, um dein Konto zu aktivieren. Schau ggf. im Spam-Ordner nach.</p>
                  {info && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">{info}</div>}
                  {error && <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}
                  <button onClick={handleResend} disabled={resendCooldown > 0 || resending} className="text-sm text-[#970000] font-semibold hover:underline disabled:text-gray-400 disabled:no-underline mb-3 inline-flex items-center gap-1.5">
                    {resending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {resendCooldown > 0 ? `Erneut senden in ${resendCooldown}s` : 'E-Mail erneut senden'}
                  </button>
                  <div><button onClick={() => switchMode('login')} className="text-sm text-gray-500 hover:text-gray-700">Zurück zur Anmeldung</button></div>
                </div>
              )}

              {/* ── FORGOT PASSWORD ── */}
              {mode === 'forgot' && (
                <>
                  <button onClick={() => switchMode('login')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                    Zurück
                  </button>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Passwort vergessen?</h3>
                  <p className="text-sm text-gray-500 mb-5">Gib deine E-Mail ein und wir senden dir einen Reset-Link.</p>
                  {error && <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}
                  {info && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">{info}</div>}
                  <form onSubmit={handleForgot} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail</label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="deine@email.de" required autoComplete="email" />
                    </div>
                    <button type="submit" disabled={submitting} className="w-full py-2.5 bg-[#970000] hover:bg-[#7a0000] text-white font-semibold rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 text-sm">
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      {submitting ? 'Wird gesendet…' : 'Reset-Link senden'}
                    </button>
                  </form>
                </>
              )}

              {/* ── LOGIN / REGISTER ── */}
              {(mode === 'login' || mode === 'register') && (
                <>
                  {/* Tabs */}
                  <div role="tablist" aria-label="Anmeldeoptionen" className="flex bg-gray-100 rounded-xl p-1 mb-6">
                    {(['login', 'register'] as const).map(t => (
                      <button key={t} role="tab" aria-selected={mode === t} onClick={() => switchMode(t)}
                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${mode === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        {t === 'login' ? 'Anmelden' : 'Registrieren'}
                      </button>
                    ))}
                  </div>

                  {/* OAuth */}
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <button onClick={() => handleProvider('google')} disabled={!!providerLoading || submitting}
                      className="flex items-center justify-center gap-2 py-2.5 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-xl text-sm font-medium text-gray-700 transition-all disabled:opacity-50">
                      {providerLoading === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
                      Google
                    </button>
                    <button onClick={() => handleProvider('apple')} disabled={!!providerLoading || submitting}
                      className="flex items-center justify-center gap-2 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
                      {providerLoading === 'apple' ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleIcon />}
                      Apple
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="text-xs text-gray-400 font-medium">oder mit E-Mail</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>

                  {error && <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}
                  {info && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">{info}</div>}

                  {/* LOGIN */}
                  {mode === 'login' && (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="deine@email.de" required autoComplete="email" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-sm font-medium text-gray-700">Passwort</label>
                          <button type="button" onClick={() => switchMode('forgot')} className="text-xs text-[#970000] font-medium hover:underline">Vergessen?</button>
                        </div>
                        <div className="relative">
                          <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={inputClass + ' pr-10'} placeholder="••••••••" required autoComplete="current-password" />
                          <button type="button" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      {email && (
                        <button type="button" onClick={handleResend} disabled={resending || resendCooldown > 0} className="text-xs text-gray-500 hover:text-[#970000] disabled:text-gray-400 inline-flex items-center gap-1.5 transition-colors">
                          {resending && <Loader2 className="h-3 w-3 animate-spin" />}
                          {resendCooldown > 0 ? `Bestätigungs-Mail erneut senden (${resendCooldown}s)` : 'Bestätigungs-Mail nicht erhalten?'}
                        </button>
                      )}
                      <button type="submit" disabled={submitting || !!providerLoading}
                        className="w-full py-2.5 bg-[#970000] hover:bg-[#7a0000] text-white font-semibold rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 text-sm">
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        {submitting ? 'Wird angemeldet…' : 'Anmelden'}
                      </button>
                    </form>
                  )}

                  {/* REGISTER */}
                  {mode === 'register' && (
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Vorname</label>
                          <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} placeholder="Vorname" required autoComplete="given-name" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nachname</label>
                          <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} placeholder="Mustermann" required autoComplete="family-name" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="deine@email.de" required autoComplete="email" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Passwort</label>
                        <div className="relative">
                          <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={inputClass + ' pr-10'} placeholder="Sicheres Passwort wählen" required autoComplete="new-password" />
                          <button type="button" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {password.length > 0 && (
                          <div className="mt-2.5 space-y-2">
                            <div className="flex gap-1">
                              {[1, 2, 3, 4].map(i => (
                                <div key={i} className={`flex-1 h-1 rounded-full transition-colors ${i <= passwordScore ? passwordScore <= 1 ? 'bg-red-500' : passwordScore === 2 ? 'bg-amber-500' : passwordScore === 3 ? 'bg-yellow-500' : 'bg-emerald-500' : 'bg-gray-100'}`} />
                              ))}
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                              {[{ key: 'length', label: 'Min. 8 Zeichen' }, { key: 'uppercase', label: 'Großbuchstabe' }, { key: 'number', label: 'Zahl' }, { key: 'special', label: 'Sonderzeichen' }].map(check => {
                                const ok = passwordChecks[check.key as keyof typeof passwordChecks];
                                return (
                                  <div key={check.key} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
                                    {ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <Circle className="h-3.5 w-3.5 shrink-0" />}
                                    {check.label}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        Nach der Registrierung bekommst du eine Bestätigungs-E-Mail. Anschließend aktiviert unser Team dein Konto.
                      </p>
                      <button type="submit" disabled={submitting || !!providerLoading}
                        className="w-full py-2.5 bg-[#970000] hover:bg-[#7a0000] text-white font-semibold rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 text-sm">
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        {submitting ? 'Wird registriert…' : 'Konto erstellen'}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>

            <p className="text-center text-xs text-gray-400 mt-5">
              Fragen?{' '}
              <a href="https://www.instagram.com/arabic.roots/" className="text-[#970000] hover:underline" target="_blank" rel="noopener noreferrer">
                Schreib uns auf Instagram
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginPage;
